import { ChevronsUpDownIcon } from 'lucide-react'
import { type ComponentPropsWithoutRef, forwardRef } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SidebarMenuButton } from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import { getInitials } from './sidebar-org-switcher-helpers.util'

interface Organization {
  id: string
  name: string
  slug: string | null
  imageUrl: string | null
}

interface SidebarOrgSwitcherTriggerProps
  extends ComponentPropsWithoutRef<typeof SidebarMenuButton> {
  activeOrganization: Organization | null
  planDisplay: string
}

export const SidebarOrgSwitcherTrigger = forwardRef<
  HTMLButtonElement,
  SidebarOrgSwitcherTriggerProps
>(({ activeOrganization, planDisplay, ...props }, ref) => {
  const orgName = activeOrganization?.name ?? 'Select Organization'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <SidebarMenuButton
          ref={ref}
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          {...props}
        >
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={activeOrganization?.imageUrl ?? undefined} alt={orgName} />
            <AvatarFallback className="rounded-lg">
              {activeOrganization ? getInitials(activeOrganization.name) : 'ORG'}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{orgName}</span>
            <span className="truncate text-xs text-muted-foreground">{planDisplay}</span>
          </div>
          <ChevronsUpDownIcon className="ml-auto size-4" />
        </SidebarMenuButton>
      </TooltipTrigger>
      <TooltipContent side="right">{orgName}</TooltipContent>
    </Tooltip>
  )
})

SidebarOrgSwitcherTrigger.displayName = 'SidebarOrgSwitcherTrigger'
